/**
 * AiGeneratePlanModal — wizard de 3 pasos para que el coach genere un plan
 * completo con IA (Claude Haiku 4.5).
 *
 * Flujo:
 *   1. Inputs: seleccionar cliente + ajustes (días/sem, equipamiento,
 *      preferencias, targets de macros opcionales).
 *   2. Loading: 5-15 segundos mientras el modelo genera.
 *   3. Preview + acciones: ver el plan generado y aplicar a un plan
 *      existente (o crear uno nuevo) — o descartar / regenerar.
 *
 * Cuota: se chequea ANTES del paso 1 con getAiQuota(). Si el coach está en
 * tier free o agotó las generaciones del mes, se muestra paywall.
 *
 * Costo registrado en `al_ai_generation` (backend). El frontend no lo
 * muestra al coach (no le interesa) — solo el `remaining_quota` post-éxito.
 */
import { useEffect, useMemo, useState } from "react";
import { Icon } from "../../lib/Icon";
import { useToast } from "../../lib/toast";
import { getAiQuota, generatePlanWithAi, MUSCLE_GROUPS, WEEK_DAYS, type AiEquipmentKind, type AiGeneratePlanInput, type AiGeneratePlanResult, type AiQuota } from "../../api/ai";
import { getClient, listClients, type ClientFull } from "../../api/clients";
import { loadFoodCatalog, type Food } from "../../api/foods";
import { ApiError } from "../../api/client";
import { HealthDisclaimer } from "../../lib/HealthDisclaimer";

interface AiGeneratePlanModalProps {
  /** Cliente pre-seleccionado (cuando el modal se abre desde un plan que ya
   *  tiene cliente o desde CreatePlanModal con cliente lockeado). */
  lockedClient?: ClientFull | null;
  /** Plan al que aplicar (si está editando un plan existente). null =
   *  preview standalone, el coach decide después qué hacer. */
  planId?: number | null;
  /** Toggles iniciales — útil cuando el modal se abre desde el botón
   *  "Generar dieta con IA" de la sección Nutrición, queremos solo nutrición
   *  pre-tildada (training=false). Defaults ambos true. */
  initialIncludeTraining?: boolean;
  initialIncludeNutrition?: boolean;
  onClose: () => void;
  /** Llamado cuando el coach acepta el plan generado. El padre persiste
   *  (creando un plan nuevo o actualizando uno existente). */
  onApply: (planDoc: any, sourceClient: ClientFull) => void | Promise<void>;
}

type Step = "inputs" | "loading" | "preview" | "paywall";

export function AiGeneratePlanModal({
  lockedClient = null,
  planId = null,
  initialIncludeTraining = true,
  initialIncludeNutrition = true,
  onClose,
  onApply,
}: AiGeneratePlanModalProps) {
  const toast = useToast();
  const [step, setStep] = useState<Step>("inputs");
  const [quota, setQuota] = useState<AiQuota | null>(null);

  // Cliente seleccionado
  const [clients, setClients] = useState<ClientFull[] | null>(null);
  const [clientsErr, setClientsErr] = useState<string | null>(null);
  const [client, setClient] = useState<ClientFull | null>(lockedClient);
  const [q, setQ] = useState("");

  // Ajustes
  const [daysPerWeek, setDaysPerWeek] = useState(4);
  const [focus, setFocus] = useState("");
  // Modo de armado de la rutina: "auto" (la IA decide) | "byDay" (el coach
  // define qué grupos trabaja cada día). daySchedule: día → grupos ([] = descanso).
  const [trainingMode, setTrainingMode] = useState<"auto" | "byDay">("auto");
  const [daySchedule, setDaySchedule] = useState<Record<string, string[]>>({});
  const [exercisesPerGroup, setExercisesPerGroup] = useState(3);
  const [equipmentKind, setEquipmentKind] = useState<AiEquipmentKind>("gym");
  const [weeks, setWeeks] = useState(4);
  const [preferences, setPreferences] = useState("");
  const [allergies, setAllergies] = useState("");
  const [preferredFoodIds, setPreferredFoodIds] = useState<number[]>([]);
  // Toggles de qué generar. Defaults vienen del caller (botones inline en
  // RoutineSection / DietSection abren el modal con solo uno tildado).
  const [includeTraining, setIncludeTraining] = useState(initialIncludeTraining);
  const [includeNutrition, setIncludeNutrition] = useState(initialIncludeNutrition);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [targetKcal, setTargetKcal] = useState("");
  const [targetProtein, setTargetProtein] = useState("");
  const [targetCarbs, setTargetCarbs] = useState("");
  const [targetFat, setTargetFat] = useState("");
  // Adaptaciones de seguridad. lowImpact fuerza el modo bajo impacto (excluye
  // saltos/equilibrio); si queda en false el backend lo deriva del IMC.
  const [lowImpact, setLowImpact] = useState(false);
  const [specialConsiderations, setSpecialConsiderations] = useState("");

  // Cache del catálogo de alimentos — para el picker de preferidos y para
  // mostrar nombres de los ids seleccionados como chips.
  const [foods, setFoods] = useState<Food[] | null>(null);
  const [showFoodPicker, setShowFoodPicker] = useState(false);

  // Resultado
  const [result, setResult] = useState<AiGeneratePlanResult | null>(null);
  const [busy, setBusy] = useState(false);

  // Cargar cuota + clientes en mount
  useEffect(() => {
    getAiQuota()
      .then((q) => {
        setQuota(q);
        if (q.limit === 0) setStep("paywall");
        else if (q.remaining === 0) setStep("paywall");
      })
      .catch(() => setQuota(null));
    if (!lockedClient) {
      listClients()
        .then((cs) => setClients(cs))
        .catch((e) => setClientsErr(e instanceof Error ? e.message : String(e)));
    }
    // Catálogo de alimentos para el picker de preferidos (cacheado)
    loadFoodCatalog()
      .then(setFoods)
      .catch(() => { /* el picker mostrará error */ });
  }, [lockedClient]);

  // Hidratar el cliente si llega como shim (solo id + full_name desde
  // PlanBuilderScreen) o si lo eligió de la lista pero falta data fresca.
  // Sin esto la validación de campos requeridos da falsos positivos:
  // ve `birthdate: undefined` aunque el cliente lo tenga en DB.
  useEffect(() => {
    if (!client) return;
    const looksShim =
      client.activity_level === undefined &&
      client.goal === undefined &&
      client.birthdate === undefined;
    if (!looksShim) return;
    let cancelled = false;
    getClient(client.id)
      .then((full) => {
        if (!cancelled) setClient(full);
      })
      .catch(() => {
        // Si falla la hidratación, el coach seguirá viendo "faltan datos"
        // — peor caso es que pase macros manuales. No es bloqueante.
      });
    return () => { cancelled = true; };
  }, [client]);

  /**
   * Chequea que el cliente tenga los datos mínimos para que el modelo pueda
   * generar macros con sentido. Devuelve `null` si todo está OK; sino, lista
   * de campos faltantes.
   *
   * Reglas:
   *   - Si el coach desactivó "Plan nutricional", no hace falta nada para
   *     macros (la rutina solo necesita objetivo + nivel, opcionales para el
   *     modelo).
   *   - Si el coach pasó los 4 macros manualmente, no hace falta calcular →
   *     no requerimos los datos del cliente.
   */
  const missingClientFields = useMemo<string[] | null>(() => {
    if (!client) return null;
    if (!includeNutrition) return null; // sin dieta no validamos macros
    if (targetKcal && targetProtein && targetCarbs && targetFat) return null;
    // Si el client viene como shim (todavía hidratando) — esperamos en silencio
    // en lugar de mostrar warning falso.
    if (client.activity_level === undefined && client.birthdate === undefined) {
      return null;
    }
    const missing: string[] = [];
    if (client.current_weight_kg == null) missing.push("peso actual");
    if (client.height_cm == null) missing.push("altura");
    if (!client.birthdate) missing.push("fecha de nacimiento");
    if (!client.sex) missing.push("sexo");
    if (!client.activity_level) missing.push("nivel de actividad");
    if (!client.goal) missing.push("objetivo");
    return missing.length ? missing : null;
  }, [client, includeNutrition, targetKcal, targetProtein, targetCarbs, targetFat]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const filteredClients = useMemo(() => {
    if (!clients) return [];
    const lower = q.trim().toLowerCase();
    return clients
      .filter((c) => c.is_active !== false)
      .filter((c) => !lower || c.full_name.toLowerCase().includes(lower));
  }, [clients, q]);

  const handleGenerate = async () => {
    if (!client) {
      toast("Elegí un cliente primero");
      return;
    }
    if (!includeTraining && !includeNutrition) {
      toast("Elegí al menos qué generar (entrenamiento o nutrición)");
      return;
    }
    if (missingClientFields && missingClientFields.length > 0) {
      toast(`Faltan datos del cliente: ${missingClientFields.join(", ")}`);
      return;
    }
    // Modo "por día": armamos el schedule (solo si entrenamiento está activo y
    // hay al menos un día con grupos). [] de grupos = descanso.
    let scheduleInput: AiGeneratePlanInput["day_schedule"] = null;
    if (includeTraining && trainingMode === "byDay") {
      const entries = WEEK_DAYS.map((d) => ({ day: d, muscles: daySchedule[d] || [] }));
      if (!entries.some((e) => e.muscles.length > 0)) {
        toast("Elegí al menos un día con grupos musculares (o usá el modo automático)");
        return;
      }
      scheduleInput = entries;
    }
    setStep("loading");
    setBusy(true);
    const input: AiGeneratePlanInput = {
      client_id: client.id,
      plan_id: planId,
      days_per_week: daysPerWeek,
      focus: focus.trim() || null,
      day_schedule: scheduleInput,
      exercises_per_group: exercisesPerGroup,
      equipment_kind: equipmentKind,
      weeks,
      preferences: preferences.trim() || null,
      allergies: allergies.trim() || null,
      preferred_food_ids: preferredFoodIds.length > 0 ? preferredFoodIds : undefined,
      include_training: includeTraining,
      include_nutrition: includeNutrition,
      target_kcal: targetKcal ? Number(targetKcal) : null,
      target_protein: targetProtein ? Number(targetProtein) : null,
      target_carbs: targetCarbs ? Number(targetCarbs) : null,
      target_fat: targetFat ? Number(targetFat) : null,
      // Solo forzamos low_impact si el coach lo tildó; si no, va null para que
      // el backend lo derive del IMC (no queremos desactivar el auto con false).
      low_impact: lowImpact ? true : null,
      special_considerations: specialConsiderations.trim() || null,
    };
    try {
      const res = await generatePlanWithAi(input);
      setResult(res);
      setStep("preview");
      // Refrescar cuota (la generación exitosa consumió 1)
      if (quota) {
        setQuota({ ...quota, used: quota.used + 1, remaining: res.remaining_quota });
      }
    } catch (e) {
      // Backend devuelve {detail: {code, message, ...}} para 403/429 con
      // motivo claro. Para otros errores fallback al .message del ApiError,
      // ya stringificado por apiFetch.
      let msg = e instanceof Error ? e.message : String(e);
      if (e instanceof ApiError) {
        const body = e.detail as any;
        const inner = body && typeof body === "object" ? (body.detail ?? body) : null;
        const code = inner && typeof inner === "object" ? (inner.code as string | undefined) : undefined;
        if (inner && typeof inner === "object" && typeof inner.message === "string") {
          msg = inner.message;
        }
        // 429 por cuota del coach agotada: marcamos la cuota local en 0 para que
        // el siguiente intento muestre paywall. OJO: el 429 por límite de cliente
        // (ai_client_monthly_limit) NO agota la cuota del coach — no la tocamos.
        if (e.status === 429 && code === "ai_quota_exceeded" && quota) {
          setQuota({ ...quota, used: quota.limit, remaining: 0 });
        }
      }
      toast(`No pude generar: ${msg}`);
      setStep("inputs");
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    if (!result || !client) return;
    setBusy(true);
    try {
      await onApply(result.plan_doc, client);
      onClose();
    } catch (e) {
      toast("No pude aplicar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="al-modal-backdrop" onClick={() => !busy && onClose()}>
      <div
        className="al-modal al-ai-gen"
        role="dialog"
        aria-label="Generar plan con IA"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              ✨ Generar plan con IA
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              {quota && quota.limit > 0
                ? `Tenés ${quota.remaining} de ${quota.limit} generaciones disponibles este mes`
                : "Generación automática con Claude Haiku 4.5"}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar" disabled={busy}>
            <Icon name="x" size={16} />
          </button>
        </div>

        {step === "paywall" && <PaywallView quota={quota} />}

        {step === "inputs" && (
          <InputsView
            client={client}
            setClient={setClient}
            lockedClient={lockedClient}
            filteredClients={filteredClients}
            clientsLoading={!clients && !clientsErr && !lockedClient}
            clientsErr={clientsErr}
            q={q}
            setQ={setQ}
            missingClientFields={missingClientFields}
            includeTraining={includeTraining}
            setIncludeTraining={setIncludeTraining}
            includeNutrition={includeNutrition}
            setIncludeNutrition={setIncludeNutrition}
            daysPerWeek={daysPerWeek}
            setDaysPerWeek={setDaysPerWeek}
            focus={focus}
            setFocus={setFocus}
            trainingMode={trainingMode}
            setTrainingMode={setTrainingMode}
            lowImpact={lowImpact}
            setLowImpact={setLowImpact}
            specialConsiderations={specialConsiderations}
            setSpecialConsiderations={setSpecialConsiderations}
            daySchedule={daySchedule}
            setDaySchedule={setDaySchedule}
            exercisesPerGroup={exercisesPerGroup}
            setExercisesPerGroup={setExercisesPerGroup}
            equipmentKind={equipmentKind}
            setEquipmentKind={setEquipmentKind}
            weeks={weeks}
            setWeeks={setWeeks}
            preferences={preferences}
            setPreferences={setPreferences}
            allergies={allergies}
            setAllergies={setAllergies}
            preferredFoodIds={preferredFoodIds}
            setPreferredFoodIds={setPreferredFoodIds}
            foods={foods}
            onOpenFoodPicker={() => setShowFoodPicker(true)}
            advancedOpen={advancedOpen}
            setAdvancedOpen={setAdvancedOpen}
            targetKcal={targetKcal}
            setTargetKcal={setTargetKcal}
            targetProtein={targetProtein}
            setTargetProtein={setTargetProtein}
            targetCarbs={targetCarbs}
            setTargetCarbs={setTargetCarbs}
            targetFat={targetFat}
            setTargetFat={setTargetFat}
          />
        )}

        {step === "loading" && <LoadingView />}

        {step === "preview" && result && client && (
          <PreviewView result={result} client={client} />
        )}

        <div className="al-ai-gen-foot">
          {step === "paywall" && (
            <>
              <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
              <div style={{ flex: 1 }} />
              <button
                className="btn btn-primary"
                onClick={() => {
                  // Navegar al config / pricing (link directo al hash)
                  window.location.hash = "#config";
                  onClose();
                }}
              >
                Ver planes
              </button>
            </>
          )}
          {step === "inputs" && (
            <>
              <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
                Cancelar
              </button>
              <div style={{ flex: 1 }} />
              <button
                className="btn btn-primary"
                onClick={handleGenerate}
                disabled={
                  !client || busy ||
                  (!includeTraining && !includeNutrition) ||
                  !!(missingClientFields && missingClientFields.length)
                }
                title={
                  !client
                    ? "Elegí un cliente primero"
                    : (!includeTraining && !includeNutrition)
                    ? "Tildá al menos entrenamiento o nutrición"
                    : missingClientFields && missingClientFields.length
                    ? `Faltan datos del cliente: ${missingClientFields.join(", ")}`
                    : "Generar plan"
                }
              >
                ✨ Generar plan
              </button>
            </>
          )}
          {step === "loading" && (
            <div className="muted tiny" style={{ padding: "12px 20px" }}>
              Un plan completo toma hasta ~1 minuto…
            </div>
          )}
          {step === "preview" && (
            <>
              <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
                Descartar
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setResult(null);
                  setStep("inputs");
                }}
                disabled={busy || (quota?.remaining ?? 0) === 0}
                title={
                  (quota?.remaining ?? 0) === 0
                    ? "Sin generaciones disponibles para regenerar"
                    : "Volver atrás y reintentar con otros ajustes"
                }
              >
                Regenerar
              </button>
              <div style={{ flex: 1 }} />
              <button
                className="btn btn-primary"
                onClick={handleApply}
                disabled={busy}
              >
                {busy ? "Aplicando…" : "Aplicar al plan"}
              </button>
            </>
          )}
        </div>
      </div>

      {showFoodPicker && foods && (
        <FoodPreferencesPicker
          allFoods={foods}
          selectedIds={preferredFoodIds}
          onSave={(ids) => {
            setPreferredFoodIds(ids);
            setShowFoodPicker(false);
          }}
          onClose={() => setShowFoodPicker(false)}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Subvistas
// ----------------------------------------------------------------------

function PaywallView({ quota }: { quota: AiQuota | null }) {
  const isFreeTier = quota?.tier === "free" || quota?.limit === 0;
  return (
    <div className="al-ai-gen-body" style={{ textAlign: "center", padding: "32px 24px" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>✨</div>
      <h4 style={{ margin: "0 0 8px", fontFamily: "var(--font-display)" }}>
        {isFreeTier
          ? "La generación con IA es una feature pago"
          : "Llegaste al límite del mes"}
      </h4>
      <p className="muted" style={{ margin: "0 auto 16px", maxWidth: 420, lineHeight: 1.5 }}>
        {isFreeTier ? (
          <>Tu plan <b>Free</b> no incluye generación automática con IA. Subí a <b>Solo</b> ($49.000/mes) para 15 generaciones mensuales, <b>Studio</b> ($89.000/mes) para 50, o <b>Unlimited</b> ($149.000/mes) para 150.</>
        ) : (
          <>Ya usaste tus {quota?.limit} generaciones de este mes. Resetea el primer día del próximo mes — o subí de plan para más.</>
        )}
      </p>
      {quota && (
        <div className="tiny muted">
          Plan actual: <b>{quota.tier}</b> · Usado: {quota.used}/{quota.limit}
        </div>
      )}
    </div>
  );
}

function LoadingView() {
  return (
    <div className="al-ai-gen-body" style={{ textAlign: "center", padding: "48px 24px" }}>
      <div className="al-ai-gen-spinner" />
      <h4 style={{ margin: "16px 0 4px", fontFamily: "var(--font-display)" }}>
        Generando tu plan…
      </h4>
      <p className="tiny muted">
        Claude está pensando — rutina, dieta, cardio, suplementos y notas. Aguantá unos segundos.
      </p>
    </div>
  );
}

interface InputsViewProps {
  client: ClientFull | null;
  setClient: (c: ClientFull | null) => void;
  lockedClient: ClientFull | null;
  filteredClients: ClientFull[];
  clientsLoading: boolean;
  clientsErr: string | null;
  q: string;
  setQ: (v: string) => void;
  /** Si el cliente seleccionado no tiene los datos mínimos para calcular
   *  macros, lista de campos faltantes (ej. ["peso actual", "altura"]).
   *  Null = cliente OK o sin cliente seleccionado todavía. */
  missingClientFields: string[] | null;
  includeTraining: boolean;
  setIncludeTraining: (v: boolean) => void;
  includeNutrition: boolean;
  setIncludeNutrition: (v: boolean) => void;
  daysPerWeek: number;
  setDaysPerWeek: (n: number) => void;
  focus: string;
  setFocus: (v: string) => void;
  trainingMode: "auto" | "byDay";
  setTrainingMode: (v: "auto" | "byDay") => void;
  lowImpact: boolean;
  setLowImpact: (v: boolean) => void;
  specialConsiderations: string;
  setSpecialConsiderations: (v: string) => void;
  daySchedule: Record<string, string[]>;
  setDaySchedule: (v: Record<string, string[]>) => void;
  exercisesPerGroup: number;
  setExercisesPerGroup: (n: number) => void;
  equipmentKind: AiEquipmentKind;
  setEquipmentKind: (v: AiEquipmentKind) => void;
  weeks: number;
  setWeeks: (n: number) => void;
  preferences: string;
  setPreferences: (v: string) => void;
  allergies: string;
  setAllergies: (v: string) => void;
  preferredFoodIds: number[];
  setPreferredFoodIds: (ids: number[]) => void;
  foods: Food[] | null;
  onOpenFoodPicker: () => void;
  advancedOpen: boolean;
  setAdvancedOpen: (v: boolean) => void;
  targetKcal: string;
  setTargetKcal: (v: string) => void;
  targetProtein: string;
  setTargetProtein: (v: string) => void;
  targetCarbs: string;
  setTargetCarbs: (v: string) => void;
  targetFat: string;
  setTargetFat: (v: string) => void;
}

/** Etiquetas humanas del enum AiEquipmentKind. Reflejan las opciones del
 *  dropdown del modal. Tienen que matchear lo que mapea el backend
 *  (EQUIPMENT_KIND_LABEL) para que el modelo entienda lo mismo. */
const EQUIPMENT_KIND_OPTIONS: Array<{ value: AiEquipmentKind; label: string; hint: string }> = [
  { value: "gym", label: "Gym completo", hint: "Barras, mancuernas, máquinas, poleas, smith" },
  { value: "gym_residencial", label: "Gym de conjunto residencial", hint: "Mancuernas livianas + 1 multifuncional, sin barras ni rack" },
  { value: "casa_mancuernas", label: "Casa con mancuernas y bandas", hint: "Mancuernas, bandas, kettlebell, peso corporal" },
  { value: "casa_peso_corporal", label: "Casa — solo peso corporal", hint: "Calistenia pura, sin equipamiento" },
  { value: "crossfit", label: "Crossfit / funcional", hint: "Kettlebells, bandas, cuerda, trineo, balón medicinal" },
];

function InputsView(p: InputsViewProps) {
  return (
    <div className="al-ai-gen-body">
      {!p.lockedClient && (
        <div className="al-ai-gen-section">
          <div className="al-ai-gen-section-title">Cliente</div>
          {p.clientsErr && <div className="al-staff-login-err">{p.clientsErr}</div>}
          {p.clientsLoading && <div className="muted tiny">Cargando clientes…</div>}
          {!p.clientsLoading && !p.clientsErr && (
            <>
              <div className="al-create-plan-search">
                <Icon name="search" size={14} />
                <input
                  type="text"
                  value={p.q}
                  onChange={(e) => p.setQ(e.target.value)}
                  placeholder="Buscar cliente por nombre…"
                  autoFocus
                />
              </div>
              <div className="al-create-plan-clients" style={{ maxHeight: 200 }}>
                {p.filteredClients.length === 0 ? (
                  <div className="muted tiny" style={{ padding: 16, textAlign: "center" }}>
                    Sin resultados
                  </div>
                ) : (
                  p.filteredClients.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={"al-create-plan-client-row" + (p.client?.id === c.id ? " is-selected" : "")}
                      onClick={() => p.setClient(c)}
                    >
                      <div className="al-create-plan-client-avatar">
                        {(c.full_name || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="al-create-plan-client-info">
                        <div className="al-create-plan-client-name">{c.full_name}</div>
                        <div className="tiny muted">
                          {c.sex === "F" ? "Mujer" : c.sex === "M" ? "Hombre" : "—"}
                          {c.current_weight_kg ? ` · ${c.current_weight_kg} kg` : ""}
                          {c.goal ? ` · ${c.goal}` : ""}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
      {p.lockedClient && (
        <div className="al-ai-gen-section">
          <div className="al-ai-gen-section-title">Cliente</div>
          <div className="al-ai-gen-client-card">
            <Icon name="user" size={14} /> Plan para <b>&nbsp;{p.lockedClient.full_name}</b>
          </div>
        </div>
      )}

      {/* Qué generar — checkboxes para elegir entrenamiento, nutrición, ambos.
          Si ambos quedan apagados, el botón Generar queda bloqueado. */}
      <div className="al-ai-gen-section">
        <div className="al-ai-gen-section-title">¿Qué generar?</div>
        <div className="al-ai-gen-toggles">
          <label className={"al-ai-gen-toggle" + (p.includeTraining ? " is-on" : "")}>
            <input
              type="checkbox"
              checked={p.includeTraining}
              onChange={(e) => p.setIncludeTraining(e.target.checked)}
            />
            <div className="al-ai-gen-toggle-icon">🏋️</div>
            <div className="al-ai-gen-toggle-body">
              <div className="al-ai-gen-toggle-label">Plan de entrenamiento</div>
              <div className="tiny muted">Rutina semanal + cardio</div>
            </div>
          </label>
          <label className={"al-ai-gen-toggle" + (p.includeNutrition ? " is-on" : "")}>
            <input
              type="checkbox"
              checked={p.includeNutrition}
              onChange={(e) => p.setIncludeNutrition(e.target.checked)}
            />
            <div className="al-ai-gen-toggle-icon">🥗</div>
            <div className="al-ai-gen-toggle-body">
              <div className="al-ai-gen-toggle-label">Plan nutricional</div>
              <div className="tiny muted">Dieta 7 días + suplementos</div>
            </div>
          </label>
        </div>
        {!p.includeTraining && !p.includeNutrition && (
          <div className="tiny" style={{ color: "var(--danger)", marginTop: 4 }}>
            Tildá al menos uno para poder generar.
          </div>
        )}
      </div>

      {/* Warning si el cliente seleccionado no tiene los datos mínimos para
          que el modelo pueda calcular macros con sentido. Bloquea el botón
          Generar arriba. */}
      {p.client && p.missingClientFields && p.missingClientFields.length > 0 && (
        <div className="al-ai-gen-missing-warning">
          <div className="al-ai-gen-missing-hd">
            <Icon name="bell" size={14} />
            <b>Faltan datos del cliente para que la IA calcule bien la dieta</b>
          </div>
          <div className="tiny" style={{ marginTop: 4 }}>
            Sin <b>{p.missingClientFields.join(", ")}</b> el modelo no puede armar macros confiables.
          </div>
          <div className="al-ai-gen-missing-actions">
            <button
              type="button"
              className="link-btn tiny"
              onClick={() => {
                // El form de cliente vive como modal aparte — abrimos el hash
                // de Clientes con el cliente seleccionado.
                window.location.hash = `#/clients?edit=${p.client!.id}`;
              }}
            >
              Completar perfil del cliente →
            </button>
            <span className="tiny muted">
              o pasá los <b>macros objetivo manualmente</b> abajo
            </span>
          </div>
        </div>
      )}

      {p.includeTraining && (
      <div className="al-ai-gen-section">
        <div className="al-ai-gen-section-title">Ajustes del entrenamiento</div>

        {/* Modo: automático (la IA decide el split) o por día (el coach define
            qué grupos trabaja cada día). */}
        <div className="al-ai-mode-toggle">
          <button
            type="button"
            className={"al-ai-mode-btn" + (p.trainingMode === "auto" ? " is-on" : "")}
            onClick={() => p.setTrainingMode("auto")}
          >
            <b>Automático</b>
            <span className="tiny muted">La IA arma el split de la semana</span>
          </button>
          <button
            type="button"
            className={"al-ai-mode-btn" + (p.trainingMode === "byDay" ? " is-on" : "")}
            onClick={() => p.setTrainingMode("byDay")}
          >
            <b>Personalizar por día</b>
            <span className="tiny muted">Vos elegís qué grupos trabaja cada día</span>
          </button>
        </div>

        <div className="al-ai-gen-grid">
          {p.trainingMode === "auto" && (
            <label className="al-food-custom-field">
              <span className="tiny" style={{ fontWeight: 600 }}>Días por semana</span>
              <select value={p.daysPerWeek} onChange={(e) => p.setDaysPerWeek(Number(e.target.value))}>
                {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n} {n === 1 ? "día" : "días"}</option>)}
              </select>
            </label>
          )}
          <label className="al-food-custom-field">
            <span className="tiny" style={{ fontWeight: 600 }}>Semanas</span>
            <select value={p.weeks} onChange={(e) => p.setWeeks(Number(e.target.value))}>
              {[1, 2, 3, 4, 6, 8, 12].map((n) => <option key={n} value={n}>{n} {n === 1 ? "semana" : "semanas"}</option>)}
            </select>
          </label>
          {p.trainingMode === "byDay" && (
            <label className="al-food-custom-field">
              <span className="tiny" style={{ fontWeight: 600 }}>Ejercicios por grupo</span>
              <select value={p.exercisesPerGroup} onChange={(e) => p.setExercisesPerGroup(Number(e.target.value))}>
                {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          )}
        </div>

        {p.trainingMode === "auto" && (
          <label className="al-food-custom-field">
            <span className="tiny" style={{ fontWeight: 600 }}>Foco principal</span>
            <input
              type="text"
              className="al-input"
              value={p.focus}
              onChange={(e) => p.setFocus(e.target.value)}
              placeholder="Ej. Glúteo y pierna · Tren superior · Cuerpo completo"
            />
          </label>
        )}

        {p.trainingMode === "byDay" && (
          <div className="al-ai-daysplan">
            {WEEK_DAYS.map((day) => {
              const sel = p.daySchedule[day] || [];
              return (
                <div key={day} className={"al-ai-dayrow" + (sel.length === 0 ? " is-rest" : "")}>
                  <div className="al-ai-dayrow-name">
                    {day}
                    {sel.length === 0 && <span className="tiny muted"> · Descanso</span>}
                  </div>
                  <div className="al-ai-dayrow-groups">
                    {MUSCLE_GROUPS.map((g) => {
                      const on = sel.includes(g);
                      return (
                        <button
                          key={g}
                          type="button"
                          className={"al-ai-group-chip" + (on ? " is-on" : "")}
                          onClick={() => {
                            const next = { ...p.daySchedule };
                            const cur = next[day] || [];
                            next[day] = on ? cur.filter((x) => x !== g) : [...cur, g];
                            p.setDaySchedule(next);
                          }}
                        >
                          {g}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="tiny muted" style={{ marginTop: 4 }}>
              Un día sin grupos = descanso (no entra en la rutina). La dieta se genera igual para los 7 días.
            </div>
          </div>
        )}

        <label className="al-food-custom-field">
          <span className="tiny" style={{ fontWeight: 600 }}>Equipamiento disponible</span>
          <select
            value={p.equipmentKind}
            onChange={(e) => p.setEquipmentKind(e.target.value as AiEquipmentKind)}
          >
            {EQUIPMENT_KIND_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="tiny muted" style={{ marginTop: 2 }}>
            {EQUIPMENT_KIND_OPTIONS.find((o) => o.value === p.equipmentKind)?.hint}
          </span>
        </label>

        {/* Adaptaciones y seguridad — bajo impacto (excluye pliometría/equilibrio
            del catálogo) + consideraciones libres que se inyectan al prompt. */}
        <div style={{ marginTop: 12 }}>
          <div className="al-ai-gen-section-title">Adaptaciones y seguridad</div>
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer", padding: "4px 0" }}>
            <input
              type="checkbox"
              checked={p.lowImpact}
              onChange={(e) => p.setLowImpact(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span className="tiny">
              <b>Priorizar bajo impacto</b> — excluye saltos, pliometría y ejercicios de equilibrio.
              Se activa solo para clientes con IMC alto; tildalo para forzarlo (ej. vértigo, problemas articulares).
            </span>
          </label>
          <label className="al-food-custom-field" style={{ marginTop: 6 }}>
            <span className="tiny" style={{ fontWeight: 600 }}>Consideraciones especiales (opcional)</span>
            <textarea
              className="al-input"
              rows={2}
              value={p.specialConsiderations}
              onChange={(e) => p.setSpecialConsiderations(e.target.value)}
              placeholder="Ej. Sin trabajo de piso · cuidar rodilla derecha · evitar impacto en articulaciones"
            />
          </label>
        </div>
      </div>
      )}

      {p.includeNutrition && (
      <div className="al-ai-gen-section">
        <div className="al-ai-gen-section-title">Ajustes de la dieta</div>
        <label className="al-food-custom-field">
          <span className="tiny" style={{ fontWeight: 600 }}>Preferencias alimenticias</span>
          <input
            type="text"
            className="al-input"
            value={p.preferences}
            onChange={(e) => p.setPreferences(e.target.value)}
            placeholder="Ej. Vegetariana · Mediterránea · Sin lácteos"
          />
        </label>
        <label className="al-food-custom-field">
          <span className="tiny" style={{ fontWeight: 600 }}>Alergias / intolerancias</span>
          <input
            type="text"
            className="al-input"
            value={p.allergies}
            onChange={(e) => p.setAllergies(e.target.value)}
            placeholder="Ej. Maní · Gluten · Mariscos"
          />
        </label>

        {/* Alimentos preferidos — selector contra food_library. Si el coach
            no elige nada, el modelo recibe un mix balanceado por categoría.
            Si elige, esos alimentos van priorizados en el snippet del prompt. */}
        <div className="al-ai-gen-foods">
          <div className="al-ai-gen-foods-hd">
            <span className="tiny" style={{ fontWeight: 600 }}>
              Alimentos preferidos del cliente
              {p.preferredFoodIds.length > 0 && (
                <span className="tiny muted">&nbsp;· {p.preferredFoodIds.length} elegidos</span>
              )}
            </span>
            <button
              type="button"
              className="link-btn tiny"
              onClick={p.onOpenFoodPicker}
              disabled={!p.foods}
            >
              {p.preferredFoodIds.length > 0 ? "Editar selección" : "+ Elegir alimentos del catálogo"}
            </button>
          </div>
          {p.preferredFoodIds.length === 0 ? (
            <div className="tiny muted">
              Opcional — si no elegís nada, la IA usa un mix balanceado del catálogo (proteínas, carbohidratos, frutas, verduras, etc.).
            </div>
          ) : (
            <div className="al-ai-gen-foods-chips">
              {p.preferredFoodIds.map((id) => {
                const f = p.foods?.find((x) => x.id === id);
                if (!f) return null;
                return (
                  <span key={id} className="chip">
                    {f.name}
                    <button
                      type="button"
                      className="al-ai-gen-chip-x"
                      onClick={() => p.setPreferredFoodIds(p.preferredFoodIds.filter((x) => x !== id))}
                      title="Quitar"
                    >
                      <Icon name="x" size={10} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <button
          type="button"
          className="al-ai-gen-advanced-toggle"
          onClick={() => p.setAdvancedOpen(!p.advancedOpen)}
        >
          {p.advancedOpen ? "▾" : "▸"} Macros objetivo (opcional — sin esto, el modelo calcula)
        </button>
        {p.advancedOpen && (
          <div className="al-ai-gen-grid al-ai-gen-grid-4">
            <label className="al-food-custom-field">
              <span className="tiny">Kcal</span>
              <input type="number" className="al-input" value={p.targetKcal} onChange={(e) => p.setTargetKcal(e.target.value)} placeholder="1800" />
            </label>
            <label className="al-food-custom-field">
              <span className="tiny">Prot (g)</span>
              <input type="number" className="al-input" value={p.targetProtein} onChange={(e) => p.setTargetProtein(e.target.value)} placeholder="140" />
            </label>
            <label className="al-food-custom-field">
              <span className="tiny">Carb (g)</span>
              <input type="number" className="al-input" value={p.targetCarbs} onChange={(e) => p.setTargetCarbs(e.target.value)} placeholder="180" />
            </label>
            <label className="al-food-custom-field">
              <span className="tiny">Grasa (g)</span>
              <input type="number" className="al-input" value={p.targetFat} onChange={(e) => p.setTargetFat(e.target.value)} placeholder="60" />
            </label>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function PreviewView({ result, client }: { result: AiGeneratePlanResult; client: ClientFull }) {
  const doc = result.plan_doc || {};
  const cover = doc.cover || {};
  const routine = doc.routine || {};
  const diet = doc.diet || {};
  const days = (routine.weeks?.[0]?.days || []) as any[];
  const dietDays = (diet.days || []) as any[];
  return (
    <div className="al-ai-gen-body">
      <div className="al-ai-gen-result-hd">
        <h4 style={{ margin: 0, fontFamily: "var(--font-display)" }}>
          {cover.programTitle || "Plan generado"}
        </h4>
        <div className="tiny muted">
          Para <b>{client.full_name}</b> · {cover.duration || "—"} · Generado en {(result.duration_ms / 1000).toFixed(1)}s
        </div>
      </div>

      <div className="al-ai-gen-result-grid">
        {/* Solo mostramos la card de rutina si el modelo realmente generó días.
            Si el coach desactivó entrenamiento, days=[] y omitimos el card. */}
        {days.length > 0 && (
          <div className="al-ai-gen-result-card">
            <div className="al-pb-side-eyebrow">RUTINA</div>
            <ul className="al-ai-gen-day-list">
              {days.map((d, i) => (
                <li key={i}>
                  <b>{d.label}</b>
                  <span className="tiny muted">
                    {d.focus ? ` · ${d.focus}` : ""}
                    {d.exercises?.length ? ` · ${d.exercises.length} ejercicios` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {dietDays.length > 0 && (
          <div className="al-ai-gen-result-card">
            <div className="al-pb-side-eyebrow">DIETA</div>
            <ul className="al-ai-gen-day-list">
              {dietDays.map((d, i) => (
                <li key={i}>
                  <b>{d.label}</b>
                  <span className="tiny muted">
                    {d.meals?.length ? ` · ${d.meals.length} comidas` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {days.length === 0 && dietDays.length === 0 && (
          <div className="al-ai-gen-result-card" style={{ gridColumn: "1 / -1" }}>
            <div className="muted tiny">
              El modelo no generó contenido. Probá de nuevo con otra configuración.
            </div>
          </div>
        )}
      </div>

      <HealthDisclaimer audience="coach" style={{ marginTop: 12 }} />

      <div className="al-ai-gen-result-meta">
        <span className="tiny muted">
          📊 {result.tokens_in.toLocaleString()} tokens in · {result.tokens_out.toLocaleString()} out
          {result.tokens_cache_read > 0 && ` · ${result.tokens_cache_read.toLocaleString()} cache hit`}
        </span>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// FoodPreferencesPicker — selector contra food_library
// ----------------------------------------------------------------------

interface FoodPreferencesPickerProps {
  allFoods: Food[];
  selectedIds: number[];
  onSave: (ids: number[]) => void;
  onClose: () => void;
}

/** Modal sub-secundario (z-index encima del AiGeneratePlanModal) que
 *  permite buscar alimentos por nombre + filtrar por categoría y agregar
 *  hasta 20. Se confirma con "Guardar". */
function FoodPreferencesPicker({ allFoods, selectedIds, onSave, onClose }: FoodPreferencesPickerProps) {
  const [draft, setDraft] = useState<Set<number>>(new Set(selectedIds));
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");

  // Categorías únicas del catálogo (orden alfabético).
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const f of allFoods) if (f.category) set.add(f.category);
    return Array.from(set).sort();
  }, [allFoods]);

  const filtered = useMemo(() => {
    const lower = search.trim().toLowerCase();
    return allFoods
      .filter((f) => !categoryFilter || f.category === categoryFilter)
      .filter((f) => !lower || f.name.toLowerCase().includes(lower))
      .slice(0, 100);
  }, [allFoods, search, categoryFilter]);

  const toggle = (id: number) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 20) next.add(id); // cap a 20 para no inflar el prompt
      return next;
    });
  };

  return (
    <div className="al-modal-backdrop" onClick={onClose} style={{ zIndex: 110 }}>
      <div
        className="al-modal al-ai-foods-picker"
        role="dialog"
        aria-label="Elegir alimentos preferidos"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 16 }}>
              Alimentos preferidos del cliente
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              {draft.size}/20 elegidos · la IA los priorizará al armar la dieta
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="al-ai-foods-filters">
          <div className="al-create-plan-search" style={{ flex: 1 }}>
            <Icon name="search" size={14} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar alimento por nombre…"
              autoFocus
            />
          </div>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="">Todas las categorías</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="al-ai-foods-list">
          {filtered.length === 0 ? (
            <div className="muted tiny" style={{ padding: 16, textAlign: "center" }}>
              Sin resultados
            </div>
          ) : (
            filtered.map((f) => {
              const on = draft.has(f.id);
              return (
                <button
                  key={f.id}
                  type="button"
                  className={"al-ai-foods-row" + (on ? " is-selected" : "")}
                  onClick={() => toggle(f.id)}
                >
                  <Icon name={on ? "check" : "plus"} size={12} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="al-ai-foods-name">{f.name}</div>
                    <div className="tiny muted">
                      {f.category || "—"} · {f.calories} kcal · {f.protein}P / {f.carbs}C / {f.fat}F por {f.base_quantity}{f.base_unit}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="al-ai-gen-foot">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDraft(new Set())}
            disabled={draft.size === 0}
          >
            Limpiar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onSave(Array.from(draft))}
          >
            Guardar {draft.size > 0 && `(${draft.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}
